import makeErrorFromUnknown from '@/functions/makeErrorFromUnknown';
import config from '@/modules/config/functions/config';
import {
  BaseError,
  CoreAuthUnauthorizedError,
  UserGetDataProcessingError,
  UserGetFetchError,
  UserGetGeneralError,
  UserGetInternalServerError,
  UserGetRequestError,
  UserGetServerConnectionError,
} from '@/modules/errors/constants/error-class-map.gen';
import logger from '@/modules/logger/constants/logger';
import attachCommonWretchErrorHandlers from '@/modules/query-client/functions/attachCommonWretchErrorHandlers';
import createBaseWretch from '@/modules/query-client/functions/createBaseWretch';
import createCommonWretchErrorHandlerMap from '@/modules/query-client/functions/createCommonWretchErrorHandlerMap';
import createQueryOptions from '@/modules/query-client/functions/createQueryOptions';
import handleWretchFetchError from '@/modules/query-client/functions/handleWretchFetchError';
import type { UserProvidedUseQueryOptions } from '@/modules/query-client/types/UserProvidedUseQueryOptions';
import type { GetUserResponse } from '@/modules/users/types/responses/GetUserResponse';
import { UserSchema, type User } from '@/modules/users/types/User';
import { isEmptyString } from '@thisisdmg/tirecloud-patterns/v2';
import { type } from 'arktype';
import { omit } from 'lodash-es';

/**
 * Create query options for useUserQuery hook
 * @param param0 - Query options
 * @param param0.wretch - Wretch instance to use for the query
 * @returns Query options for useUserQuery hook
 */
export default function createUseUserQueryOptions({
  wretch: wretchInstance = createBaseWretch(),
  ...options
}: UserProvidedUseQueryOptions<User>) {
  return createQueryOptions({
    queryKey: ['user', 'me'],
    queryFn: async () => {
      return attachCommonWretchErrorHandlers(
        wretchInstance.url('api/v1/user').get(),
        createCommonWretchErrorHandlerMap([
          ['badRequest', UserGetRequestError],
          ['timeout', UserGetServerConnectionError],
          ['internalError', UserGetInternalServerError],
          [502, UserGetServerConnectionError],
          [503, UserGetServerConnectionError],
          [504, UserGetServerConnectionError],
          ['fetchError', UserGetFetchError],
          ['unauthorized', CoreAuthUnauthorizedError],
        ])
      )
        .json<GetUserResponse>()
        .then((user) => {
          const maybeUser = UserSchema({
            ...omit(user, 'Company', 'zipcode', 'role'),
            company: user.Company,
            zipCode: user.zipcode,
            role: isEmptyString(user.role) ? 'ROLE_WEBSHOP_USER' : user.role,
          });
          if (maybeUser instanceof type.errors)
            throw new UserGetDataProcessingError(new Error(maybeUser.summary));
          return maybeUser;
        })
        .catch((error) => {
          handleWretchFetchError(error, 'user.get', UserGetFetchError);
          logger.error('user.get', error);
          if (error instanceof BaseError) throw error;
          throw new UserGetGeneralError(makeErrorFromUnknown(error));
        });
    },
    retry(failureCount, error) {
      if (error instanceof UserGetDataProcessingError) return false;
      return failureCount < config('misc.defaultQueryRetryCount', 3);
    },
    ...omit(options, 'variables'),
  });
}
